import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { updateSystemSetting } from "../../../services";
import { SystemSetting } from "../../../types/system-setting.type";
import SettingValueInput from "./SettingValueInput";
import { AFFECTS_REPORTS_WARNING, settingLabel, settingPairError } from "./settingMeta";
import {
  SettingDraft,
  describeConstraints,
  formatSettingValue,
  isSameSettingValue,
  parseSettingDraft,
  toSettingDraft,
} from "./settingValue";

const TITLE_SX = { fontWeight: 700, pr: 6 } as const;
const CLOSE_BUTTON_SX = { position: "absolute", right: 8, top: 8 } as const;
const KEY_SX = { fontFamily: "monospace" } as const;
const ACTIONS_SX = { p: 2 } as const;

interface SettingEditDialogProps {
  setting: SystemSetting;
  /** Why the value is not in use now, when another setting makes it moot. */
  note: string | null;
  /** Every setting, for rules between keys. */
  settings: SystemSetting[];
  onClose: () => void;
  onSaved: () => void;
}

/** Mounted per edit, so every open starts from the value saved now. */
export default function SettingEditDialog({
  setting,
  note,
  settings,
  onClose,
  onSaved,
}: Readonly<SettingEditDialogProps>) {
  const [draft, setDraft] = useState<SettingDraft>(() => toSettingDraft(setting.value));
  const [saving, setSaving] = useState<boolean>(false);

  const parsed = useMemo(() => {
    const result = parseSettingDraft(setting.value_type, setting.constraints, draft, setting.key);
    if (result.error !== null) return result;
    const pairError = settingPairError(setting.key, result.value, settings);
    return pairError ? { value: null, error: pairError } : result;
  }, [setting.value_type, setting.constraints, draft, setting.key, settings]);
  const isUnchanged = parsed.error === null && isSameSettingValue(parsed.value, setting.value);
  const canSave = parsed.error === null && !isUnchanged;
  const constraintText = describeConstraints(
    setting.value_type,
    setting.constraints,
    setting.key,
  );
  const defaultText = `ค่าเริ่มต้นของระบบ: ${formatSettingValue(setting.default_value, setting.key)}`;

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    if (parsed.error !== null) return;
    setSaving(true);
    try {
      await updateSystemSetting(setting.key, { value: parsed.value });
      onSaved();
    } catch {
      // The axios interceptor shows the server's message; the draft stays for a correction.
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={TITLE_SX}>
        {settingLabel(setting.key)}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={saving}
          sx={CLOSE_BUTTON_SX}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary" sx={KEY_SX}>
              {setting.key}
            </Typography>
            <Typography variant="body2">{setting.description}</Typography>
          </Stack>
          {setting.affects_reports && (
            <Alert severity="warning">{AFFECTS_REPORTS_WARNING}</Alert>
          )}
          {note && <Alert severity="info">{note}</Alert>}
          <SettingValueInput
            setting={setting}
            draft={draft}
            error={parsed.error}
            helperText={constraintText}
            disabled={saving}
            onChange={setDraft}
          />
          <Typography variant="caption" color="text.secondary">
            {defaultText}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={ACTIONS_SX}>
        <Button variant="outlined" onClick={handleClose} disabled={saving}>
          ยกเลิก
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave} loading={saving}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
